import { useEffect, useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { EmptyState, LoadingBlock } from "@/components/common/States";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, Legend } from "recharts";
import api from "@/lib/api";

function fmt(n) {
  if (n == null) return "0";
  if (n >= 1e6) return (n/1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n/1e3).toFixed(1) + "k";
  return String(n);
}

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/analytics/overview"); setData(data); }
      catch { setData({ kpis: {}, trend: [], per_network: [], top_posts: [] }); }
    })();
  }, []);

  if (data === null) return <div><PageHeader title="Analytics" subtitle="Performance across every account you publish to." /><LoadingBlock rows={6} /></div>;

  const k = data.kpis || {};
  const trend = (data.trend || []).map(r => ({ ...r, day: r.day.slice(5) }));

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Performance across every account you publish to." />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard testid="an-kpi-reach" label="Reach" value={fmt(k.total_reach)} />
        <KpiCard testid="an-kpi-impressions" label="Impressions" value={fmt(k.total_impressions)} />
        <KpiCard testid="an-kpi-engagements" label="Engagements" value={fmt(k.total_engagements)} tone="positive" />
        <KpiCard testid="an-kpi-followers" label="Followers" value={fmt(k.followers)} />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="trend-chart-card">
          <div className="mb-3 text-sm font-semibold text-[#0B2B33]">30-day trend</div>
          {trend.length === 0 ? <EmptyState title="Not enough data yet" body="Publish more to see the trend." /> :
            <div className="h-72 min-h-[288px] w-full">
              <ResponsiveContainer width="100%" height="100%" minHeight={288}>
                <LineChart data={trend} margin={{ left: -12, right: 8 }}>
                  <CartesianGrid stroke="#E4E9EF" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: "#8A97A0", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#8A97A0", fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E4E9EF" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="reach" stroke="#1F5FD8" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="engagements" stroke="#16A46B" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          }
        </div>

        <div className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="per-network-card">
          <div className="mb-3 text-sm font-semibold text-[#0B2B33]">By network</div>
          {(data.per_network || []).length === 0 ? <EmptyState title="No network data" /> :
            <div className="h-72 min-h-[288px] w-full">
              <ResponsiveContainer width="100%" height="100%" minHeight={288}>
                <BarChart data={data.per_network} margin={{ left: -12, right: 8 }}>
                  <CartesianGrid stroke="#E4E9EF" vertical={false} />
                  <XAxis dataKey="network" tick={{ fill: "#8A97A0", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#8A97A0", fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E4E9EF" }} />
                  <Bar dataKey="reach" fill="#1F5FD8" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="engagements" fill="#16A46B" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          }
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="top-posts-card">
        <div className="mb-3 text-sm font-semibold text-[#0B2B33]">Top posts</div>
        {(data.top_posts || []).length === 0 ? <EmptyState title="No top posts yet" body="Once posts are published, we'll rank them here." /> :
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
                <th className="pb-2">Title</th><th className="pb-2">Status</th><th className="pb-2">Reach</th><th className="pb-2">Engagements</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E9EF]">
              {data.top_posts.map(p => (
                <tr key={p.post_id}>
                  <td className="py-2 text-[#0B2B33]">{p.title}</td>
                  <td className="py-2 text-[#43535E]">{p.status}</td>
                  <td className="py-2 text-[#43535E]">{fmt(p.reach)}</td>
                  <td className="py-2 text-[#43535E]">{fmt(p.engagements)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
}
